import { AddressForm } from "@/components/address/AddressForm";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Checkbox, Col, Form, Input, Popconfirm, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useCreateInstitution,
  useDeleteInstitution,
  useFetchInstitution,
  useUpdateInstitution,
} from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import { rowGutter } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { InstitutionEnum, InstitutionType, institutionTypeTMap } from "../../enums";
import { INSTITUTION_ROUTE_PATHS } from "../../paths";
import { CreateUpdateInstitution, Institution } from "../../types";
import { convertInstitutionToCreateUpdateObject } from "../../utils";
import InstitutionTypeTag from "../InstitutionTypeTag";

interface Props {
  institution?: Institution;
}

const colSpan = 6;
const colSpanSmall = 3;

export const InstitutionForm = ({ institution }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const updateInstitution = useUpdateInstitution();
  const createInstitution = useCreateInstitution();
  const deleteInstitution = useDeleteInstitution();

  const [form] = Form.useForm<CreateUpdateInstitution>();
  const type = Form.useWatch("type", form);
  const branchOfficeOfId = Form.useWatch("branchOfficeOfId", form);
  const nonBankSubject = Form.useWatch("nonBankSubject", form);

  const fetchInstitution = useFetchInstitution();

  useEffect(() => {
    if (institution) {
      form.setFieldsValue(convertInstitutionToCreateUpdateObject(institution));
    }
  }, [institution, form]);

  const handleFormFinish = (values: CreateUpdateInstitution) => {
    if (institution) {
      updateInstitution.mutate(
        { id: institution.id, object: values },
        {
          onError: (error) => {
            setErrorsToForm(form, "institution.attrs", error.violations);
          },
        },
      );
      return;
    }

    createInstitution.mutate(values, {
      onSuccess: (data) => {
        navigate(generatePath(INSTITUTION_ROUTE_PATHS.detail.to, { id: data.id }));
      },
      onError: (error) => {
        setErrorsToForm(form, "institution.attrs", error.violations);
      },
    });
  };

  const handleDeleteClick = async () => {
    if (institution) {
      await deleteInstitution.mutateAsync(institution.id);

      navigate(INSTITUTION_ROUTE_PATHS.list.to);
    }
  };

  const handleChangeBranchOfficeOf = async (institutionId?: UUID) => {
    if (!institutionId) {
      form.setFieldsValue({ bankCode: undefined, bankSwift: undefined, bankAcronym: undefined });
      return;
    }

    const { bankCode, bankSwift, bankAcronym } = await fetchInstitution.mutateAsync(institutionId);

    form.setFieldsValue({ bankCode, bankSwift, bankAcronym });
  };

  return (
    <>
      <ItemCreatedUpdatedInfoView item={institution} className="margin-bottom-medium" />

      <Form form={form} layout="vertical" onFinish={handleFormFinish} name="institutionCreateUpdateForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="type" label={t("institution.enums.institutionType._label")} rules={[validations.notNull]}>
              <Select
                {...selectTagsStandardProps(institutionTypeTMap)}
                disabled={!!institution}
                tagRender={(props) => (
                  <InstitutionTypeTag
                    type={InstitutionType[props.value as keyof typeof InstitutionType]}
                    closable={props.closable}
                    onClose={props.onClose}
                  />
                )}
                options={Object.keys(InstitutionType).map((type) => ({
                  value: type,
                  label: <InstitutionTypeTag type={InstitutionType[type as keyof typeof InstitutionType]} />,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="institutionEnum"
              label={t("institution.enums.institutionEnum._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(InstitutionEnum).map((institutionEnum) => ({
                  value: institutionEnum,
                  label: t("institution.enums.institutionEnum." + institutionEnum),
                }))}
              />
            </Form.Item>
          </Col>

          {type === InstitutionType.BANK && (
            <Col span={colSpan}>
              <Form.Item
                name="nonBankSubject"
                className="form-item-without-label"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("institution.attrs.nonBankSubject")}</Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="name"
              label={t("institution.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 64)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan * 2}>
            <Form.Item
              name="officialName"
              label={t("institution.attrs.officialName")}
              rules={[validations.notBlank, validations.size(1, 1024)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="companyRegistrationNumber"
              label={t("institution.attrs.companyRegistrationNumber")}
              rules={[validations.notBlank, validations.crn]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {type === InstitutionType.BANK && !nonBankSubject && (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <InstitutionSelect
                formItemProps={{
                  name: "branchOfficeOfId",
                  label: t("institution.attrs.branchOfficeOfId"),
                }}
                optionsProps={{ filterType: InstitutionType.BANK }}
                selectProps={{
                  onChange: handleChangeBranchOfficeOf,
                  allowClear: true,
                  loading: fetchInstitution.isPending,
                }}
              />
            </Col>
            <Col span={colSpanSmall}>
              <Form.Item
                name="bankCode"
                label={t("institution.attrs.bankCode")}
                rules={[validations.notBlank, validations.length(4), validations.numeric]}
              >
                <Input disabled={!!branchOfficeOfId} />
              </Form.Item>
            </Col>

            <Col span={colSpanSmall}>
              <Form.Item
                name="bankSwift"
                label={t("institution.attrs.bankSwift")}
                rules={[validations.notBlank, validations.size(4, 8)]}
                normalize={upperCaseStringNormalizeFunction}
              >
                <Input disabled={!!branchOfficeOfId} />
              </Form.Item>
            </Col>

            <Col span={colSpanSmall}>
              <Form.Item
                name="bankAcronym"
                label={t("institution.attrs.bankAcronym")}
                rules={[validations.notBlank, validations.max(10)]}
                normalize={upperCaseStringNormalizeFunction}
              >
                <Input disabled={!!branchOfficeOfId} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <AddressForm formItemsNamePath={["address"]} label={t("institution.attrs.address")} />

        <div className="margin-top-medium">
          <Button className="margin-right-tiny" type="primary" htmlType="submit" icon={<AntIcon type="save" />}>
            {t("common.save")}
          </Button>

          {institution && (
            <Popconfirm
              title={t("institution.titles.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={handleDeleteClick}
            >
              <Button icon={<AntIcon type="delete" />} danger>
                {t("common.delete")}
              </Button>
            </Popconfirm>
          )}
        </div>
      </Form>
    </>
  );
};
